import type { Vehicle } from '@cruz-com/lookup';
import { useLocalStorageState } from 'ahooks';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { HiMinus, HiPlus } from 'react-icons/hi2';
import { twMerge } from 'tailwind-merge';
import { vehicleApi } from '@/api';
import { Checkbox, Text } from '@/components';
import { Section } from './vehicleFilterSection';
import { VehicleMakeModelListBox } from './vehicleMakeModelListBox';
type mmQuery = {
  make?: string;
  model?: string | null;
  trim?: string | null;
};
type Props = {
  category?: string;
  bodyTypeId?: string | number;
  defaultOpen?: boolean;
  className?: string;
  name: string;
  title: string;
  otherQuery: Vehicle.Query;
  mmt: string;
  makeOptions: Vehicle.MakeModelOption;
  onChange: (val: string | null) => void;
} & Omit<React.ComponentPropsWithoutRef<'select'>, 'onChange'>;
const ignoreFilters = ['mmt1', 'mmt2', 'mmt3', 'make', 'model', 'trim'];
const mmStyle = 'text-sm mb-2 w-[60px] font-medium';
export function MakeModeTrim({
  defaultOpen,
  className,
  name,
  title,
  category,
  bodyTypeId,
  otherQuery,
  mmt,
  makeOptions,
  onChange
}: Props) {
  function getQueryFromMmt() {
    if (!mmt) {
      return {
        make: ''
      };
    }
    const y = _.split(mmt, ',');
    return {
      make: y[0],
      model: y[1],
      trim: y[2]
    };
  }
  const sectionRef = useRef<null | HTMLDivElement>(null);
  const [query, setQuery] = useState<mmQuery>(getQueryFromMmt());
  const [isMakeChanged, setIsMakeChanged] = useState<boolean>(false); // used for scroll into view
  const [modelOption, setModelOption] = useState<Vehicle.MakeModelOption | null>(null);
  const [modelKeyword, setModelKeyword] = useState('');
  const [showOtherModel, setShowOtherModel] = useLocalStorageState<boolean>(`mmt-${name}`, {
    defaultValue: false
  });
  const [trimOption, setTrimOption] = useState<string[] | null>(null);

  // otherQuery Changed
  useEffect(() => {
    if (query?.make) {
      getModelOption();
    } else {
      setModelOption({});
      setTrimOption([]);
    }
  }, [otherQuery]);

  // mmt changed
  useEffect(() => {
    setQuery(getQueryFromMmt());
  }, [mmt]);

  // update parent
  useEffect(() => {
    if (query?.make) {
      onChange(_.compact([query.make, query?.model, query?.trim]).join(','));
    } else {
      onChange(null);
    }
  }, [query]);

  // make changed
  function makeChanged(val: string) {
    setQuery({
      make: val,
      model: null,
      trim: null
    });
  }
  useEffect(() => {
    setModelOption(null);
    setTrimOption(null);
    setIsMakeChanged(true);
    if (query?.make) {
      getModelOption();
    }
  }, [query?.make]);

  // models
  // get model option
  async function getModelOption() {
    const q = {
      ..._.omit(otherQuery, ignoreFilters),
      make: query.make
    };
    if (bodyTypeId) {
      _.assign(q, {
        body_type: bodyTypeId
      });
    }
    const d = await vehicleApi.getModelOption(q, category);
    setModelOption(d);
  }
  useEffect(() => {
    if (!_.isEmpty(modelOption) && isMakeChanged) {
      sectionRef?.current?.scrollIntoView({
        behavior: 'smooth'
      });
      setIsMakeChanged(false);
    }
  }, [modelOption]);
  function modelSelected(model: string, val_0: boolean) {
    const x: string[] = query?.model ? _.split(query.model as string, '|') : [];
    const y_0 = val_0 ? _.concat(x, [model]) : _.without(x, model);
    setQuery({
      ...query,
      model: _.join(y_0, '|')
    });
  }
  useEffect(() => {
    if (_.isEmpty(query?.model)) {
      setQuery({
        ...query,
        trim: null
      });
      setTrimOption([]);
    }
  }, [query?.model]);
  const filteredPopular = useCallback(() => {
    if (_.isEmpty(modelKeyword)) {
      return modelOption?.popular;
    }
    return _.filter(modelOption?.popular, (x_0: string) => x_0.toLowerCase().includes(modelKeyword.toLowerCase()));
  }, [modelOption?.popular, modelKeyword]);
  const filteredOther = useCallback(() => {
    if (_.isEmpty(modelKeyword)) {
      return modelOption?.other;
    }
    return _.filter(modelOption?.other, (x_1: string) => x_1.toLowerCase().includes(modelKeyword.toLowerCase()));
  }, [modelOption?.other, modelKeyword]);
  const renderModels = () => {
    if (!modelOption || _.isEmpty(modelOption?.popular) && _.isEmpty(modelOption?.other)) {
      return null;
    }
    const showTitle = !_.isEmpty(modelOption?.popular) && !_.isEmpty(modelOption?.other);

    // popular models
    const renderPopularModels = () => {
      if (_.isEmpty(modelOption?.popular)) {
        return null;
      }
      return <fieldset className='mt-4'>
          {showTitle && <legend className='mt-3 w-full border-b pb-1.5 text-sm font-medium'>
              Popular Models <span className='font-light'>({_.size(modelOption?.popular)})</span>
            </legend>}
          <ul className='mt-1 grid grid-cols-1 gap-x-4 md:grid-cols-2'>
            {_.map(filteredPopular(), (modelName, idx) => {
            const x_2 = _.split(query?.model, '|');
            return <li key={`pop-model-${idx}`}>
                  <Checkbox name={modelName} label={modelName} checked={x_2.includes(modelName)} onChange={val_1 => modelSelected(modelName, val_1)} />
                </li>;
          })}
          </ul>
        </fieldset>;
    };
    const renderOtherModels = () => {
      if (_.isEmpty(modelOption?.other)) {
        return null;
      }
      return <fieldset className='mt-3'>
          {showTitle && <>
              <button type='button' className='items-bottom mt-4 flex w-full justify-between border-b pb-1.5 text-sm font-medium' onClick={() => setShowOtherModel(!showOtherModel)} aria-expanded={showOtherModel} aria-controls='other-models-list'>
                <div className='font-medium'>
                  Other Models <span className='font-light'>({_.size(modelOption?.other)})</span>
                </div>
                {showOtherModel ? <HiMinus className='size-4' aria-hidden='true' /> : <HiPlus className='size-4' aria-hidden='true' />}
              </button>
              <div aria-hidden='true' className={twMerge('mt-1 text-sm font-light text-zinc-500', showOtherModel && 'hidden')}>
                Click "Other Models" to see more ...
              </div>
            </>}
          <ul id='other-models-list' className={twMerge('mt-1 grid grid-cols-1 gap-x-4 md:grid-cols-2', !showTitle || showOtherModel ? '' : 'hidden')}>
            {_.map(filteredOther(), (modelName_0, idx_0) => {
            const x_3 = _.split(query?.model, '|');
            return <li key={`other-model-${idx_0}`}>
                  <Checkbox name={modelName_0} label={modelName_0} checked={x_3.includes(modelName_0)} onChange={val_2 => modelSelected(modelName_0, val_2)} />
                </li>;
          })}
          </ul>
        </fieldset>;
    };
    return <fieldset className='flex w-full items-start'>
        <legend className={mmStyle}>Models:</legend>
        <div className='flex-1'>
          <div className='mb-3 w-full md:w-[200px]'>
            <Text placeholder='Search Model' compact={true} name='mf' type='text' value={modelKeyword} onChange={e => setModelKeyword(e.target.value || '')} />
          </div>
          {renderPopularModels()}
          {renderOtherModels()}
        </div>
      </fieldset>;
  };

  // trims
  useEffect(() => {
    setTrimOption(null);
    if (!_.isEmpty(query?.model)) {
      getTrimOption();
    }
  }, [query?.model]);

  // get trim options
  async function getTrimOption() {
    const q_0 = {
      ..._.omit(otherQuery, ignoreFilters),
      make: query.make,
      model: query.model
    };
    if (bodyTypeId) {
      _.assign(q_0, {
        body_type: bodyTypeId
      });
    }
    const d_0 = await vehicleApi.getTrimOption(q_0, category);
    setTrimOption(d_0);
  }
  function trimSelected(trim: string, val_3: boolean) {
    const x_4: string[] = query?.trim ? _.split(query.trim as string, '|') : [];
    const y_1 = val_3 ? _.concat(x_4, [trim]) : _.without(x_4, trim);
    setQuery({
      ...query,
      trim: _.join(y_1, '|')
    });
  }
  const renderTrims = () => {
    if (_.isEmpty(trimOption)) {
      return null;
    }
    return <fieldset className='flex w-full items-start'>
        <legend className={mmStyle}>Trims:</legend>
        <ul className='mt-1 grid flex-1 grid-cols-1 gap-x-4 md:grid-cols-2'>
          {_.map(trimOption, (trimName, idx_1) => {
          const x_5 = _.split(query?.trim, '|');
          return <li key={`trim-${idx_1}`}>
                <Checkbox name={trimName} label={trimName} checked={x_5.includes(trimName)} onChange={val_4 => trimSelected(trimName, val_4)} />
              </li>;
        })}
        </ul>
      </fieldset>;
  };
  return <div ref={sectionRef}>
      <Section name={name} title={title} hasValue={!_.isEmpty(query?.make)} className={className} defaultOpen={defaultOpen}>
        <div className='flex w-full flex-col gap-y-3 pb-2'>
          <fieldset className='flex items-start'>
            <legend className={mmStyle}>Make:</legend>
            <div className='w-[220px]'>
              <VehicleMakeModelListBox value={query?.make || 'Any'} allowAny={true} option={makeOptions} onChange={val_5 => makeChanged(val_5 as string)} />
            </div>
          </fieldset>
          {renderModels()}
          {renderTrims()}
        </div>
      </Section>
    </div>;
}